import { useCallback, useEffect, useLayoutEffect, useRef, useState, type DragEvent, type KeyboardEvent, type MouseEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { BurrowNav } from '../../components/BurrowNav'
import { useToast } from '../../components/Toast'
import { errorText, MobileSheet } from '../../components/ui'
import { PHOTO_MIME, THOUGHT_MIME, ThoughtsPanel } from '../../components/ThoughtsPanel'
import { HistoryPanel } from '../../components/HistoryPanel'
import { SourceWalkBar } from '../../components/SourceWalkBar'
import { useSourceWalk } from './useSourceWalk'
import { NarrationCard } from '../../components/audio/NarrationCard'
import { VoiceMemoView } from '../../components/audio/VoiceMemoView'
import { EditableBlockView } from '../../components/editor/EditableBlock'
import { QuoteAttributionField } from '../../components/editor/QuoteAttributionField'
import { DividerView, DragGrip, ImageBlockView, LinkCardBlockView, ThoughtBlockView, type ColumnSizer } from '../../components/editor/BlockViews'
import { ImageEditor, type FrameTarget } from '../../components/ImageEditor'
import { MarginNoteView, SPAN_INPUT_LABEL } from '../../components/editor/MarginNoteView'
import { UnfoldCard } from '../../components/editor/UnfoldCard'
import { SceneView, SCENE_COLORS } from '../../components/editor/SceneView'
import { SlashMenu } from '../../components/editor/SlashMenu'
import { InsertMenu, LinkForm } from '../../components/editor/InsertMenu'
import { CollectionPicker } from '../../components/editor/CollectionPicker'
import { CollectionBlockView } from '../../components/editor/CollectionBlockView'
import { collectionSnapshot } from '../../components/CollectionLinkCard'
import { ThoughtPicker } from '../../components/editor/ThoughtPicker'
import { LinkEditor, SelectionToolbar, type BlockKind, type FormatAction, type SelectionBox } from '../../components/editor/SelectionToolbar'
import { announceInput, formatState, toggleHighlight, toggleInline, type FormatState } from '../../components/editor/format'
import { BLOCK_MIME } from '../../components/editor/ImagePlacementToolbar'
import { choiceForKey, choiceHeadingLevel, isLinkKey, NOT_IN_A_CELL, type InsertChoice, type ThoughtChoice } from '../../components/editor/insertChoices'
import {
  caretAtEnd,
  caretAtStart,
  currentRange,
  linkAt,
  placeCaret,
  placeCaretAt,
  rangeFromOffsets,
  selectionOffsets,
  textBeforeCaret,
  unlinkOffsets,
  wrapInLink,
  wrapInNoteMark,
} from '../../components/editor/caret'
import { useBackend, useSession } from '../../lib/session'
import { useMediaQuery, useNow, useStoredToggle } from '../../lib/hooks'
import { DESK } from '../../lib/layout'
import {
  ensureTrailingText,
  headingLevel,
  isBlockEmpty,
  isEditable,
  isOutOfFlow,
  isThoughtLike,
  isUntouched,
  newBlockId,
  newUnfoldAnchor,
  isPinnedNote,
  pinnedParagraphCount,
  pinSide,
  REFLECTION_SUBTITLE,
  reflectionBlocks,
  thoughtSnapshot,
  thoughtToBlock,
  unfoldFor,
  usedThoughtIds,
} from '../../lib/blocks'
import { countWords, dateKey, displayName, escapeHtml, hostOf, stripTags, longDate, readMinutes, startOfDay, timeAgo, weekdayDate } from '../../lib/text'
import { prepareImage } from '../../lib/images'
import { useNarrationSync } from '../../lib/useNarrationSync'
import { useAutoSnapshot } from '../../lib/useAutoSnapshot'
import type { CapturedAudio } from '../../lib/audio'
import type {
  Block,
  CollectionSummary,
  EditableBlock,
  ImageBlock,
  ImageFrame,
  ImagePlacement,
  MarginNoteBlock,
  Narration,
  PinSide,
  PullQuoteBlock,
  SceneBlock,
  Source,
  ThoughtBlock,
  ThoughtLikeBlock,
  Thought,
  UnfoldBlock,
  VoiceBlock,
  Writing,
} from '../../lib/types'
import {
  afterForBefore,
  asideAnchor,
  canPlaceBeside,
  canWrapBeside,
  imageDropTarget,
  insertAside,
  insertColumn,
  insertColumns,
  insertedAsideAnchor,
  insertWrapped,
  intoColumn,
  maxPinSpan,
  moveBeside,
  moveGroup,
  placeBeside,
  removeUnfold,
  setPinSide,
  setPinSpan,
  placeImage,
  withFrame,
  withNewPhoto,
  withWidth,
  wrapBeside,
  type Side,
} from './blockOps'
import { useComposerDoc } from './useComposerDoc'
import { isTextField, undoKey, undoOwner } from './blockHistory'
import { AddColumnBeside, besideAWrap, ColumnDropLine, ColumnGutter } from '../../components/editor/ColumnGutter'
import { BlockToolbar, ToolbarButton, ToolbarLabel } from '../../components/editor/Toolbar'
import {
  collapseEmptiedCells,
  columnGroups,
  columnMinShare,
  columnsTemplate,
  columnWidths,
  equalWidths,
  groupOf,
  isColumnsMarker,
  resizeBoundary,
  setColumnWidths,
  unwrapColumns,
} from '../../lib/columns'
import { blockEl, selectableEl, useBlockEditor } from './useBlockEditor'
import { BlockList } from './BlockList'
import { AudioDock } from './AudioDock'
import { useAudioDock, type MemoTarget } from './useAudioDock'
import { ComposerHeader } from './ComposerHeader'
import { ComposerToolBar } from './ComposerToolBar'
import { useFocusLine } from './useFocusLine'
import { useCrossBlockSelection } from './useCrossBlockSelection'
import { SheetFab } from '../../components/SidePanel'

/** Words the author selected in a paragraph, kept as text offsets (for a margin note or a link). */
interface SavedSelection {
  blockId: string
  start: number
  end: number
  words: string
}

interface PickerState {
  kind: ThoughtChoice
  /** The block it inserts after (null: the end). */
  afterId: string | null
  /** Fill this (empty) margin note with the thought rather than inserting a new one. */
  fill?: string
  /** The words that note is about, for the picker's title. */
  words?: string
}

/** The link being typed for selected words. */
interface Linking {
  sel: SavedSelection
  box: SelectionBox
  href: string
}

/** Whose image the crop & position editor is framing. */
type Cropping = { kind: 'cover' } | { kind: 'block'; id: string }

/** The crop & position editor's box for each image placement. */
const PLACEMENT_TARGET: Record<ImagePlacement, FrameTarget> = {
  inline: 'inline',
  'wrap-left': 'wrap',
  'wrap-right': 'wrap',
  full: 'full',
}

/** Whether the word count and read time show in the page's corner (on unless hidden). */
const WORD_COUNT_KEY = 'burrow-writings-word-count'

function blockKindOf(b: Block | undefined): BlockKind {
  if (b?.type === 'heading') return { type: 'heading', level: headingLevel(b) }
  return b?.type === 'pullquote' ? { type: 'pullquote', level: headingLevel(b) } : { type: 'text' }
}

/** Does `el` still hold the same words at the saved offsets? */
function selectionHolds(sel: SavedSelection): boolean {
  const el = blockEl(sel.blockId)
  return !!el && (el.textContent ?? '').slice(sel.start, sel.end).trim() === sel.words
}

/** The current selection if it's words inside one paragraph. */
interface ReadSelection {
  sel: SavedSelection
  box: SelectionBox
  format: FormatState
  /** No margin note's or unfold's words in it, so a new margin note can be about them. */
  canNote: boolean
  /** The link the words are in, if any. */
  link: string | null
  /** In a margin note's words (NoteText), where only Link applies. */
  note: boolean
}

/** A margin note's words (NoteText), by the note's id. */
const noteTextEl = (id: string) => document.querySelector<HTMLElement>(`[data-note-text="${id}"]`)

/** Where selected words live: a paragraph's editable, or a margin note's words. */
const wordsEl = (id: string) => blockEl(id) ?? noteTextEl(id)

/** Take the highlight off the words a margin note was about. */
function unwrapNoteMark(html: string, anchor: string): string {
  const tpl = document.createElement('template')
  tpl.innerHTML = html
  tpl.content.querySelectorAll('mark[data-note]').forEach((m) => {
    if (m.getAttribute('data-note') === anchor) m.replaceWith(...Array.from(m.childNodes))
  })
  return tpl.innerHTML
}

function readSelection(): ReadSelection | null {
  const r = currentRange()
  if (!r || r.collapsed) return null
  const node = r.commonAncestorContainer
  const at = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement
  // A margin note's words take a link too (Niki, 10/5), editable or quoted as saved.
  const noteHost = at?.closest<HTMLElement>('[data-note-text]')
  if (noteHost) return readNoteSelection(r, noteHost)
  const host = at?.closest<HTMLElement>('[data-block]')
  if (!host || host.getAttribute('contenteditable') !== 'true') return null
  const off = selectionOffsets(host)
  if (!off) return null
  const words = (host.textContent ?? '').slice(off.start, off.end).trim()
  if (!words) return null
  const canNote = !Array.from(host.querySelectorAll('span[data-unfold], mark[data-note]')).some((el) => r.intersectsNode(el))
  const rect = typeof r.getBoundingClientRect === 'function' ? r.getBoundingClientRect() : host.getBoundingClientRect()
  return {
    sel: { blockId: host.dataset.block!, start: off.start, end: off.end, words },
    box: { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width },
    format: formatState(r, host),
    canNote,
    link: linkAt(host, off.start, off.end),
    note: false,
  }
}

function readNoteSelection(r: Range, host: HTMLElement): ReadSelection | null {
  const off = selectionOffsets(host)
  if (!off) return null
  const words = (host.textContent ?? '').slice(off.start, off.end).trim()
  if (!words) return null
  const rect = typeof r.getBoundingClientRect === 'function' ? r.getBoundingClientRect() : host.getBoundingClientRect()
  return {
    sel: { blockId: host.dataset.noteText!, start: off.start, end: off.end, words },
    box: { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width },
    format: { bold: false, italic: false, highlight: false },
    canNote: false,
    link: linkAt(host, off.start, off.end),
    note: true,
  }
}

export function Composer({ initial }: { initial: Writing }) {
  const backend = useBackend()
  const { profile } = useSession()
  const toast = useToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const desk = useMediaQuery(DESK)
  const wideDesk = useMediaQuery('(min-width: 768px)')
  /** Where the tool bar ends on screen, for the thoughts panel lying over a narrow page. */
  const [barFoot, setBarFoot] = useState(48)
  const now = useNow()

  // ── Document, autosave, history, narration ─────────────────────────────
  const { doc, docRef, update, setBlocks, blockOp, gesture, undo, redo, undoState, canUndo, canRedo, saveNow, replace, saveState, savedAt, savedVersion } =
    useComposerDoc(initial)
  useAutoSnapshot(initial.id, savedVersion)
  const setNarration = useCallback((n: Narration | null) => update((d) => ({ ...d, narration: n })), [update])
  const sync = useNarrationSync(doc.narration, doc.blocks, setNarration)
  const authorName = displayName(profile)

  // ── The audio dock: record (in pieces), listen, sync, voice memos ──────
  /** A voice memo recorded (or picked) in the dock: a new one beside its paragraph, or a replacement. */
  const saveMemo = (target: MemoTarget, a: CapturedAudio) => {
    const audio = { src: a.src, duration: a.duration, peaks: a.peaks, recordedAt: a.recordedAt }
    if (target.voiceId) {
      blockOp((bs) => bs.map((b) => (b.id === target.voiceId && b.type === 'voice' ? { ...b, ...audio } : b)))
      return
    }
    const vb: VoiceBlock = { id: newBlockId(), type: 'voice', ...audio, transcript: '', label: 'aside' }
    blockOp((bs) => insertAside(bs, target.afterId, vb))
  }
  const dock = useAudioDock({ narration: doc.narration, blocks: doc.blocks, sync, onNarration: setNarration, onMemo: saveMemo, notify: toast })
  /** While the dock is open the page is for reading: no editing, no menus. */
  const locked = dock.mode !== null

  // ── Panels, sheets and inserts in progress ─────────────────────────────
  const [picker, setPicker] = useState<PickerState | null>(null)
  const [linkFormAfter, setLinkFormAfter] = useState<string | null | undefined>(undefined)
  /** The collection picker: where its card goes (null = the end), or the card it re-picks for. */
  const [collectionPick, setCollectionPick] = useState<{ afterId: string | null; replace?: string } | null>(null)
  const [sourceFormFor, setSourceFormFor] = useState<string | null>(null)
  const [dropAfter, setDropAfter] = useState<string | null>(null)
  /** Set when the drop is into a block's gutter: the dragged thing becomes a column on that side. */
  const [dropSide, setDropSide] = useState<Side | null>(null)
  /** The thing dragged is an image (Niki, 10/5): at a block's edge it wraps on that side instead of making a column. */
  const [dropWrap, setDropWrap] = useState(false)
  /** The gutter between two columns a drag is over (the marker of the cell after it): a new column there. */
  const [dropGutter, setDropGutter] = useState<string | null>(null)
  /**
   * A drop on the top half of block `dropAfter` (Composer v3): it lands just
   * before that block, i.e. after this one (or first, when this is null).
   * Undefined for the bottom half: after `dropAfter` itself.
   */
  const [dropBefore, setDropBefore] = useState<string | null | undefined>(undefined)
  /** The block being dragged by its handle (dragover can't read the drag's data). */
  const draggingRef = useRef<string | null>(null)
  // No standing thoughts column (Niki, 10/2): "/" brings thoughts to the line; the full panel opens only when asked for.
  const [panelOpen, setPanelOpen] = useState(false)
  const [focusMode, setFocusMode] = useState(false)
  const [showCount, setShowCount] = useStoredToggle(WORD_COUNT_KEY, true)
  const [panelSeed, setPanelSeed] = useState<{ text: string; at: number } | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  // The page goes inert under History. Set as an attribute: React 18 (the
  // runtime here) doesn't understand a boolean `inert` prop.
  const pageRef = useRef<HTMLElement>(null)
  /** How many times the "press again to remove" hint has shown. */
  const cardHints = useRef(0)
  useEffect(() => {
    pageRef.current?.toggleAttribute('inert', historyOpen)
  }, [historyOpen])
  const [gen, setGen] = useState(0)
  const [coverBusy, setCoverBusy] = useState(false)
  const [cropping, setCropping] = useState<Cropping | null>(null)
  const [dayCounts, setDayCounts] = useState<number[]>([])
  const [totalThoughts, setTotalThoughts] = useState<number | null>(null)
  const [selection, setSelection] = useState<ReadSelection | null>(null)
  const [linking, setLinking] = useState<Linking | null>(null)
  /** A margin note just made from selected words: its box gets the caret, and it goes again if left empty. */
  const freshNote = useRef<{ id: string; focused: boolean } | null>(null)
  const panelSearchRef = useRef<HTMLInputElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const subtitleRef = useRef<HTMLParagraphElement>(null)
  const coverInput = useRef<HTMLInputElement>(null)
  const imageInput = useRef<HTMLInputElement>(null)
  const imageAfter = useRef<string | null>(null)
  const sceneImageFor = useRef<string | null>(null)
  const replaceInput = useRef<HTMLInputElement>(null)
  const replaceImageFor = useRef<string | null>(null)

  const editor = useBlockEditor({
    docRef,
    setBlocks,
    blockOp,
    subtitleRef,
    onSlashThought: (t, blockId) => void insertThought(t, blockId),
    onRemoveBlock: (id) => removeBlock(id),
    onCardReached: () => {
      // Say it the first few times; after that the author knows.
      if (cardHints.current >= 3) return
      cardHints.current += 1
      toast('Press Backspace again to remove it')
    },
  })
  useCrossBlockSelection({ pageRef, docRef, blockOp, bumpRev: editor.bumpRev, focusLater: editor.focusLater, disabled: locked })

  useLayoutEffect(() => {
    if (titleRef.current) titleRef.current.textContent = initial.title
    if (subtitleRef.current) subtitleRef.current.textContent = initial.subtitle
  }, [initial])

  useEffect(() => {
    document.title = `${initial.title || 'Untitled'} — Writings`
  }, [initial.title])

  useEffect(() => {
    let alive = true
    Promise.all([backend.thoughtCountsByDay(7), backend.countThoughts()])
      .then(([counts, total]) => {
        if (!alive) return
        setDayCounts(counts)
        setTotalThoughts(total)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [backend])

  // Arriving from the publish sheet's "Fix": a guided walk through every
  // thought with no source, starting with that one's form (useSourceWalk).
  const openSourceFor = useCallback(
    (id: string) => {
      editor.setSelectedId(id)
      setSourceFormFor(id)
      requestAnimationFrame(() => selectableEl(id)?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const sourceWalk = useSourceWalk(doc.blocks, openSourceFor, toast)
  useEffect(() => {
    const fix = searchParams.get('fix')
    if (!fix) return
    sourceWalk.start(fix)
    const next = new URLSearchParams(searchParams)
    next.delete('fix')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Words selected in a paragraph: a small toolbar over them.
  useEffect(() => {
    const read = () => setSelection(readSelection())
    document.addEventListener('selectionchange', read)
    window.addEventListener('scroll', read, { passive: true, capture: true })
    window.addEventListener('resize', read)
    return () => {
      document.removeEventListener('selectionchange', read)
      window.removeEventListener('scroll', read, { capture: true })
      window.removeEventListener('resize', read)
    }
  }, [])

  // Opening the dock puts the editor down: no caret, selection, open menus or thoughts panel.
  useEffect(() => {
    if (!locked) return
    editor.setSelectedId(null)
    setPicker(null)
    setLinkFormAfter(undefined)
    setCollectionPick(null)
    setSourceFormFor(null)
    setPanelOpen(false)
    window.getSelection()?.removeAllRanges()
    const active = document.activeElement as HTMLElement | null
    if (active?.isContentEditable) active.blur()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked])

  // ── Inserting ──────────────────────────────────────────────────────────
  const insertThought = useCallback(
    async (t: Thought, afterId: string | null, side: Side | null = null) => {
      let src: string | null = null
      if (t.image_path) {
        src = await backend.importThoughtImage(t.image_path)
        if (!src) toast('That photo couldn’t be copied in — added its caption instead.')
      }
      const block = thoughtToBlock(t, src)
      blockOp((bs) => {
        // A photo wraps beside the block, never makes a column; anywhere in Columns it goes after them.
        if (block.type === 'image' && side && afterId) return insertWrapped(bs, afterId, side, block)
        if (block.type !== 'image' && side && afterId) return placeBeside(bs, afterId, side, [block])
        const out = insertColumn(bs, block.type === 'image' ? imageDropTarget(bs, afterId) : afterId, [block])
        const next = out[out.findIndex((b) => b.id === block.id) + 1]
        if (next && isEditable(next)) editor.focusLater(next.id, 'start')
        return out
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [backend, blockOp, toast],
  )

  // ── Formatting selected words (the selection toolbar) ──────────────────
  /** Bold, italic or highlight the selected words; the block saves them like typing. */
  const formatSelection = (sel: SavedSelection, action: FormatAction) => {
    const el = blockEl(sel.blockId)
    const range = currentRange()
    if (!el || !range || !el.contains(range.commonAncestorContainer)) return
    if (action === 'highlight') {
      toggleHighlight(range, el)
      announceInput(el)
    } else {
      toggleInline(action)
    }
    setSelection(readSelection())
  }

  /** Turn the paragraph holding the selection into a heading or pull quote (or back), keeping its words and the selection. */
  const convertEditable = (sel: SavedSelection, to: BlockKind) => {
    const el = blockEl(sel.blockId)
    if (!el) return
    const html = el.innerHTML
    blockOp((bs) =>
      bs.map((b) => {
        if (b.id !== sel.blockId || !isEditable(b)) return b
        const origin = b.fromThought ? { fromThought: b.fromThought } : {}
        if (to.type === 'heading') return { id: b.id, type: 'heading', html, level: to.level, ...origin }
        if (to.type === 'pullquote') {
          // A new size keeps who said it.
          const said = b.type === 'pullquote' && b.attribution ? { attribution: b.attribution } : {}
          return { id: b.id, type: 'pullquote', html, level: to.level, ...said, ...origin }
        }
        return { id: b.id, type: to.type, html, ...origin }
      }),
    )
    editor.bumpRev(sel.blockId)
    requestAnimationFrame(() => {
      const next = blockEl(sel.blockId)
      const r = next && sel.end > sel.start && rangeFromOffsets(next, sel.start, sel.end)
      if (!next) return
      if (!r) {
        placeCaretAt(next, sel.start)
        return
      }
      next.focus()
      const s = window.getSelection()
      s?.removeAllRanges()
      s?.addRange(r)
    })
  }

  const openPanel = () => {
    setPanelOpen(true)
    setFocusMode(false)
    requestAnimationFrame(() => panelSearchRef.current?.focus())
  }

  /** A search from Burrow's nav: here, it looks through your thoughts in the panel. */
  const searchThoughts = (text: string) => {
    setPanelSeed({ text, at: Date.now() })
    openPanel()
  }

  const insertChoice = (choice: InsertChoice, afterId: string | null) => {
    setPicker(null)
    setLinkFormAfter(undefined)
    setCollectionPick(null)
    switch (choice) {
      case 'heading1':
      case 'heading2':
      case 'heading3':
      case 'pullquote': {
        const level = choiceHeadingLevel(choice)
        const nb: Block = level ? { id: newBlockId(), type: 'heading', html: '', level } : { id: newBlockId(), type: 'pullquote', html: '' }
        blockOp((bs) => insertColumn(bs, afterId, [nb]))
        editor.focusLater(nb.id, 'start')
        return
      }
      case 'divider':
        blockOp((bs) => insertColumn(bs, afterId, [{ id: newBlockId(), type: 'divider' }]))
        return
      case 'columns': {
        const made = insertColumns(docRef.current.blocks, afterId, 2, newBlockId)
        blockOp(() => made.blocks)
        editor.focusLater(made.focusId, 'start')
        return
      }
      case 'image':
        imageAfter.current = afterId
        imageInput.current?.click()
        return
      case 'scene': {
        const sc: SceneBlock = { id: newBlockId(), type: 'scene', mode: 'solid', color: SCENE_COLORS[0][0], height: 'half', label: '', html: '' }
        blockOp((bs) => insertColumn(bs, afterId, [sc]))
        editor.setSelectedId(sc.id)
        editor.focusLater(sc.id, 'start', true)
        return
      }
      case 'linkcard':
        setLinkFormAfter(afterId)
        return
      case 'collection':
        setCollectionPick({ afterId })
        return
      case 'voice':
        dock.openMemo({ afterId, anchorId: insertedAsideAnchor(docRef.current.blocks, afterId) })
        return
      case 'thought':
      case 'margin':
        setPicker({ kind: choice, afterId })
        return
    }
  }

  // ── Margin notes on selected words; links ──────────────────────────────
  /**
   * Highlight the selected words, tie them to a new margin note beside their
   * paragraph and put the caret in it: type the note, or fill it from your
   * thoughts.
   */
  const noteOnWords = (sel: SavedSelection) => {
    const el = blockEl(sel.blockId)
    if (!el || !selectionHolds(sel)) {
      toast('Those words changed — select them again.')
      return
    }
    const anchor = newUnfoldAnchor()
    const words = wrapInNoteMark(el, sel.start, sel.end, anchor)
    if (!words) return
    const html = el.innerHTML
    const mb: MarginNoteBlock = { id: newBlockId(), type: 'margin', text: '', own: true, anchor, words }
    blockOp((bs) => insertAside(bs.map((b) => (b.id === sel.blockId && isEditable(b) ? { ...b, html } : b)), sel.blockId, mb))
    window.getSelection()?.removeAllRanges()
    setSelection(null)
    editor.setSelectedId(mb.id)
    freshNote.current = { id: mb.id, focused: false }
  }

  // The new note's box takes the caret once it's on the page.
  useEffect(() => {
    const f = freshNote.current
    if (!f || f.focused) return
    const box = selectableEl(f.id)?.querySelector<HTMLElement>('[data-note-text][contenteditable="true"], textarea')
    if (!box) return
    f.focused = true
    box.focus({ preventScroll: true })
    selectableEl(f.id)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  })

  // Left empty (the author clicked away without writing or picking a thought): it goes, and so does its highlight.
  useEffect(() => {
    const f = freshNote.current
    if (!f || editor.selectedId === f.id || picker?.fill === f.id) return
    freshNote.current = null
    const b = docRef.current.blocks.find((x) => x.id === f.id)
    if (b?.type === 'margin' && b.own && !b.text.trim()) removeBlock(f.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.selectedId, picker])

  const openLink = (found: ReadSelection) => {
    setLinking({ sel: found.sel, box: found.box, href: found.link ?? '' })
  }

  /** Put the selection back on the linked words, in their paragraph. */
  const reselect = (sel: SavedSelection) => {
    const el = wordsEl(sel.blockId)
    const r = el && rangeFromOffsets(el, sel.start, sel.end)
    if (!el || !r) return
    el.focus({ preventScroll: true })
    const s = window.getSelection()
    s?.removeAllRanges()
    s?.addRange(r)
  }

  /** Link the selected words to `raw` (a web address, or an email), or take their link off (null). */
  const applyLink = (raw: string | null) => {
    const l = linking
    setLinking(null)
    if (!l) return
    const el = wordsEl(l.sel.blockId)
    if (!el) return
    let href: string | null = null
    if (raw) {
      href = /^(https?:|mailto:)/i.test(raw) ? raw : /^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/.test(raw) ? `mailto:${raw}` : `https://${raw}`
      try {
        new URL(href)
      } catch {
        toast('That doesn’t look like a link.')
        reselect(l.sel)
        return
      }
    }
    unlinkOffsets(el, l.sel.start, l.sel.end)
    if (href) wrapInLink(el, l.sel.start, l.sel.end, href)
    announceInput(el)
    reselect(l.sel)
  }

  // ── Keyboard shortcuts in the text ─────────────────────────────────────
  /** A block's shortcut from a paragraph: headings and Quote turn this one; the rest go in after it. */
  const runShortcut = (choice: InsertChoice, block: EditableBlock, el: HTMLElement) => {
    if (editor.slash) editor.closeSlash()
    if (NOT_IN_A_CELL.has(choice) && groupOf(docRef.current.blocks, block.id)) {
      toast(
        choice === 'columns'
          ? 'Columns can’t go inside a column.'
          : choice === 'image'
            ? 'Images wrap beside the text rather than going in a column.'
            : 'A scene can’t go inside a column.',
      )
      return
    }
    const level = choiceHeadingLevel(choice)
    if (level || choice === 'pullquote') {
      const off = selectionOffsets(el)
      const at = textBeforeCaret(el).length
      const sel: SavedSelection = { blockId: block.id, start: off?.start ?? at, end: off?.end ?? at, words: '' }
      const now = blockKindOf(block)
      const nowLevel = now.type === 'text' ? null : now.level
      const to: BlockKind = level
        ? now.type === 'heading' && now.level === level
          ? { type: 'text' }
          : { type: 'heading', level }
        : now.type === 'pullquote'
          ? { type: 'text' }
          : { type: 'pullquote', level: nowLevel ?? 2 }
      convertEditable(sel, to)
      return
    }
    if (choice === 'margin') {
      const found = readSelection()
      if (found && found.sel.blockId === block.id && found.canNote) {
        noteOnWords(found.sel)
        return
      }
    }
    insertChoice(choice, block.id)
  }

  const editable = {
    ...editor.editable,
    onKeyDown: (e: KeyboardEvent<HTMLElement>, block: EditableBlock) => {
      if (!locked && isLinkKey(e)) {
        const found = readSelection()
        if (found && found.sel.blockId === block.id) {
          e.preventDefault()
          openLink(found)
          return
        }
      }
      const choice = locked ? null : choiceForKey(e)
      if (choice) {
        e.preventDefault()
        runShortcut(choice, block, e.currentTarget)
        return
      }
      editor.editable.onKeyDown(e, block)
    },
  }

  const onPicked = (p: PickerState, t: Thought | null, ownText?: string) => {
    setPicker(null)
    const snap = t ? thoughtSnapshot(t) : { text: ownText ?? '', own: true }
    switch (p.kind) {
      case 'thought': {
        if (t) {
          void insertThought(t, p.afterId)
          return
        }
        const tb: ThoughtBlock = { id: newBlockId(), type: 'thought', ...snap }
        blockOp((bs) => insertColumn(bs, p.afterId, [tb]))
        return
      }
      case 'margin': {
        if (p.fill) {
          // The note on selected words, filled from a thought: it keeps its words and highlight.
          const id = p.fill
          blockOp((bs) =>
            bs.map((b) => {
              if (b.id !== id || b.type !== 'margin') return b
              const { own: _own, ...rest } = b
              return { ...rest, ...snap } as MarginNoteBlock
            }),
          )
          return
        }
        const mb: MarginNoteBlock = { id: newBlockId(), type: 'margin', ...snap }
        blockOp((bs) => insertAside(bs, p.afterId, mb))
        return
      }
    }
  }

  const addLinkCard = (raw: string) => {
    let url = raw.trim()
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`
    try {
      new URL(url)
    } catch {
      toast('That doesn’t look like a link.')
      return
    }
    const after = linkFormAfter ?? null
    blockOp((bs) => insertColumn(bs, after, [{ id: newBlockId(), type: 'linkcard', url, title: hostOf(url) || url }]))
    setLinkFormAfter(undefined)
  }

  /** A collection picked for a new card, or for the card being changed. */
  const addCollectionCard = (c: CollectionSummary) => {
    const pick = collectionPick
    setCollectionPick(null)
    if (!pick) return
    const snap = collectionSnapshot(c)
    if (pick.replace) {
      const id = pick.replace
      blockOp((bs) => bs.map((b) => (b.id === id && b.type === 'collection' ? { id, type: 'collection', ...snap } : b)))
      return
    }
    blockOp((bs) => insertColumn(bs, pick.afterId, [{ id: newBlockId(), type: 'collection', ...snap }]))
  }

  const collectionPickerAt = (afterId: string | null) =>
    collectionPick && !collectionPick.replace && collectionPick.afterId === afterId ? (
      <CollectionPicker onPick={addCollectionCard} onCancel={() => setCollectionPick(null)} />
    ) : null

  async function uploadPicked(file: File): Promise<string | null> {
    try {
      return await backend.uploadImage(await prepareImage(file))
    } catch (e) {
      toast(errorText(e))
      return null
    }
  }

  async function onImagePicked(file: File | undefined) {
    if (!file) return
    const after = imageAfter.current
    toast('Adding image…')
    const url = await uploadPicked(file)
    // Never into Columns: picked from inside a cell, it goes after the group.
    if (url) blockOp((bs) => insertColumn(bs, imageDropTarget(bs, after), [{ id: newBlockId(), type: 'image', src: url, caption: '', placement: 'inline' }]))
  }

  async function onSceneImagePicked(file: File | undefined) {
    const id = sceneImageFor.current
    if (!file || !id) return
    toast('Adding image…')
    const url = await uploadPicked(file)
    // A new image starts unframed: the old crop was for the old picture.
    if (url) blockOp((bs) => bs.map((b) => (b.id === id && b.type === 'scene' ? withFrame({ ...b, mode: 'image', src: url }, null) : b)))
  }

  /** Another photo in an image block, through the same upload as inserting one; one undo, like any block edit. */
  async function replaceImage(id: string | null, file: File | undefined) {
    if (!file || !id || locked) return
    toast('Replacing image…')
    const url = await uploadPicked(file)
    if (url) blockOp((bs) => bs.map((b) => (b.id === id && b.type === 'image' ? withNewPhoto(b, url) : b)))
  }

  async function setCover(file: File | undefined) {
    if (!file) return
    setCoverBusy(true)
    const url = await uploadPicked(file)
    setCoverBusy(false)
    if (!url) return
    // A new cover starts unframed, and goes straight to crop & position once
    // (the way Notion offers "Reposition"); Cancel keeps the default framing.
    update((d) => ({ ...d, cover_url: url, cover_frame: null }))
    setCropping({ kind: 'cover' })
  }

  /** What the crop & position editor is framing now, or null (nothing, or it's gone). */
  const cropSubject = (): { src: string; frame: ImageFrame | null | undefined; target: FrameTarget } | null => {
    if (!cropping) return null
    if (cropping.kind === 'cover') return doc.cover_url ? { src: doc.cover_url, frame: doc.cover_frame, target: 'cover' } : null
    const b = doc.blocks.find((x) => x.id === cropping.id)
    if (b?.type === 'image') return { src: b.src, frame: b.frame, target: PLACEMENT_TARGET[b.placement ?? 'inline'] }
    if (b?.type === 'scene' && b.mode === 'image' && b.src) {
      // Scenes are only ever half height now; one saved "full" crops as it reads.
      return { src: b.src, frame: b.frame, target: 'scene-half' }
    }
    return null
  }

  const applyCrop = (frame: ImageFrame | null) => {
    const c = cropping
    setCropping(null)
    if (!c) return
    if (c.kind === 'cover') {
      update((d) => ({ ...d, cover_frame: frame }))
      return
    }
    blockOp((bs) => bs.map((b) => (b.id === c.id && (b.type === 'image' || b.type === 'scene') ? withFrame(b, frame) : b)))
  }

  const reflectOnDay = useCallback(
    async (day: number) => {
      try {
        const thoughts = await backend.listThoughts({ day, limit: 100 })
        if (!thoughts.length) return
        const d = docRef.current
        const used = usedThoughtIds(d.blocks)
        const fresh = thoughts.filter((t) => !used.has(t.id))
        const withImages = await Promise.all(
          fresh.map(async (thought) => ({
            thought,
            imageSrc: thought.image_path ? await backend.importThoughtImage(thought.image_path) : null,
          })),
        )
        if (isUntouched(d)) {
          const date = startOfDay(day)
          const title = weekdayDate(date)
          if (titleRef.current) titleRef.current.textContent = title
          if (subtitleRef.current) subtitleRef.current.textContent = REFLECTION_SUBTITLE
          const blocks = reflectionBlocks(withImages)
          update((x) => ({ ...x, title, subtitle: REFLECTION_SUBTITLE, blocks, kind: 'reflection', reflection_date: dateKey(date) }))
          editor.focusLater(blocks[0].id, 'start')
        } else {
          const inserted = reflectionBlocks(withImages).slice(1)
          // A day's thoughts go under the columns rather than into one of their cells.
          blockOp((bs) => {
            const g = groupOf(bs, editor.lastEditable.current)
            return insertColumn(bs, g ? bs[g.end].id : editor.lastEditable.current, inserted)
          })
        }
        if (!desk) setPanelOpen(false)
      } catch (e) {
        toast(errorText(e))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [backend, blockOp, toast, update, desk],
  )

  const showUsed = (thoughtId: string) => {
    const b = docRef.current.blocks.find((x) => (isThoughtLike(x) || x.type === 'image') && x.thoughtId === thoughtId)
    if (!b) return
    if (!desk) setPanelOpen(false)
    editor.setSelectedId(b.id)
    requestAnimationFrame(() => selectableEl(b.id)?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
  }

  // ── Block handlers ─────────────────────────────────────────────────────
  /** Change one block's fields: words typed into it (`op` false), or a block operation that undo steps back (`op` true). */
  const patchBlock = (id: string, patch: Partial<Block>, op = false) =>
    (op ? blockOp : setBlocks)((bs) => bs.map((b) => (b.id === id ? ({ ...b, ...patch } as Block) : b)))

  /** A margin note's words, typed or linked: `html` only while some are linked. */
  const setNoteWords = (id: string, text: string, html: string | undefined) =>
    setBlocks((bs) =>
      bs.map((b) => {
        if (b.id !== id || b.type !== 'margin') return b
        const next: MarginNoteBlock = { ...b, text }
        if (html) next.html = html
        else delete next.html
        return next
      }),
    )

  const removeBlock = (id: string) => {
    const bs = docRef.current.blocks
    const i = bs.findIndex((b) => b.id === id)
    const b = bs[i]
    if (!b) return
    editor.setSelectedId(null)
    setSourceFormFor(null)
    if (isColumnsMarker(b)) {
      // Removing columns by key keeps what's written in them.
      blockOp((list) => unwrapColumns(list, id))
      return
    }
    if (b.type === 'unfold') {
      const { touched } = removeUnfold(bs, id)
      blockOp((list) => removeUnfold(list, id).blocks)
      if (touched) editor.bumpRev(touched)
      return
    }
    if (b.type === 'margin' && b.anchor) {
      const anchor = b.anchor
      const holder = bs.find((x) => isEditable(x) && x.html.includes(`data-note="${anchor}"`))
      blockOp((list) =>
        collapseEmptiedCells(
          list,
          ensureTrailingText(
            list.filter((x) => x.id !== id).map((x) => (holder && x.id === holder.id && isEditable(x) ? { ...x, html: unwrapNoteMark(x.html, anchor) } : x)),
          ),
        ),
      )
      if (holder) editor.bumpRev(holder.id)
      if (picker?.fill === id) setPicker(null)
      return
    }
    // A column it leaves with nothing in it folds away.
    blockOp((list) => collapseEmptiedCells(list, ensureTrailingText(list.filter((x) => x.id !== id))))
    // Margin items go quietly; a column block hands the caret to the text above it.
    if (b.type === 'margin' || b.type === 'voice') return
    const prev = bs.slice(0, i).reverse().find(isEditable)
    if (prev) editor.focusLater(prev.id, 'end')
  }

  /** A margin note, pin or unfold becomes a sourced thought in the column. */
  const toColumn = (id: string) => {
    const { touched } = intoColumn(docRef.current.blocks, id)
    blockOp((bs) => intoColumn(bs, id).blocks)
    if (touched) editor.bumpRev(touched)
    editor.setSelectedId(id)
    requestAnimationFrame(() => selectableEl(id)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }))
  }

  // ── Pinning a margin note across paragraphs ────────────────────────────
  /**
   * The pin button (Niki, 10/2): it pins the note at once, across its own
   * paragraph, and opens its options with the paragraph count focused, ready
   * to type or step. On a pinned note it opens the options the same way.
   * The dotted line beside the covered paragraphs shows the reach.
   */
  const pinFocus = useRef<string | null>(null)
  const pinNote = (id: string) => {
    const b = docRef.current.blocks.find((x) => x.id === id)
    if (!b || b.type !== 'margin' || !maxPinSpan(docRef.current.blocks, id)) return
    if (!isPinnedNote(b)) blockOp((bs) => setPinSpan(bs, id, 1))
    editor.setSelectedId(id)
    pinFocus.current = id
  }
  // The count's field, once the note has moved to its track and its options are up.
  useEffect(() => {
    const id = pinFocus.current
    if (!id) return
    const field = selectableEl(id)?.querySelector<HTMLInputElement>(`input[aria-label="${SPAN_INPUT_LABEL}"]`)
    if (field) {
      pinFocus.current = null
      field.focus()
    }
  })

  /** A card becomes words; it keeps its source and date, so "Back to card" can undo it. */
  const convertThought = (b: ThoughtBlock, to: 'text' | 'pullquote') => {
    const { id: _id, type: _type, text, ...origin } = b
    blockOp((bs) => bs.map((x) => (x.id === b.id ? ({ id: b.id, type: to, html: escapeHtml(text), fromThought: origin } as Block) : x)))
    editor.bumpRev(b.id)
    editor.setSelectedId(null)
    editor.focusLater(b.id, 'end')
  }

  /** Words that were a card go back to being that card, with what they say now. */
  const backToCard = (b: EditableBlock) => {
    if (!b.fromThought) return
    const el = blockEl(b.id)
    const text = (el?.innerText ?? stripTags(b.html)).trim()
    blockOp((bs) => bs.map((x) => (x.id === b.id ? ({ ...b.fromThought, id: b.id, type: 'thought', text } as ThoughtBlock) : x)))
    editor.bumpRev(b.id)
    editor.selectBlock(b.id)
  }

  const selectable = (id: string) => ({
    selected: !locked && editor.selectedId === id,
    onSelect: () => {
      if (!locked) editor.selectBlock(id)
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (!locked) editor.onSelectableKey(e, id)
    },
    onRemove: () => removeBlock(id),
  })

  const sourcing = (id: string) => ({
    sourceFormOpen: sourceFormFor === id,
    onEditSource: () => setSourceFormFor(id),
    onCloseSource: () => setSourceFormFor(null),
    onSaveSource: (source: Source | undefined, own: boolean) => {
      patchBlock(id, { source, own } as Partial<ThoughtLikeBlock>)
      setSourceFormFor(null)
      // On the Fix walk: straight on to the next thought still missing one.
      sourceWalk.saved(id, own || !!(source?.title || source?.url))
    },
  })

  // ── Header ─────────────────────────────────────────────────────────────
  const onHeaderInput = (field: 'title' | 'subtitle') => (e: React.FormEvent<HTMLElement>) => {
    const el = e.currentTarget
    if ((el.textContent ?? '') === '' && el.innerHTML !== '') el.innerHTML = ''
    const value = el.textContent ?? ''
    update((d) => ({ ...d, [field]: value }))
  }

  const onTitleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || (e.key === 'ArrowDown' && caretAtEnd(e.currentTarget))) {
      e.preventDefault()
      if (subtitleRef.current) placeCaret(subtitleRef.current, 'start')
    }
  }

  const onSubtitleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const el = e.currentTarget
    if (e.key === 'Enter' || (e.key === 'ArrowDown' && caretAtEnd(el))) {
      e.preventDefault()
      const first = docRef.current.blocks.find(isEditable)
      if (first) placeCaret(blockEl(first.id)!, 'start')
    } else if (e.key === 'ArrowUp' && caretAtStart(el) && titleRef.current) {
      e.preventDefault()
      placeCaret(titleRef.current, 'end')
    }
  }

  // ── Drag: thoughts in from the panel, blocks moved by their handle ─────
  const lastColumnId = () => {
    const bs = docRef.current.blocks
    for (let i = bs.length - 1; i >= 0; i--) if (!isOutOfFlow(bs[i])) return bs[i].id
    return null
  }
  const onDragOver = (e: DragEvent) => {
    if (locked) return
    const types = e.dataTransfer.types
    const moving = types.includes(BLOCK_MIME)
    if (!moving && !types.includes(THOUGHT_MIME)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = moving ? 'move' : 'copy'
    const target = e.target as Element
    // An image (a block, or a photo from the panel) wraps; it never makes or joins a column (Niki, 10/5).
    const image = moving ? docRef.current.blocks.find((b) => b.id === draggingRef.current)?.type === 'image' : types.includes(PHOTO_MIME)
    setDropWrap(image)
    if (image) {
      const cols = target.closest?.('[data-cols]')
      if (cols) {
        // Over Columns: after the whole group, the line under it.
        const g = groupOf(docRef.current.blocks, cols.getAttribute('data-cols'))
        setDropGutter(null)
        setDropAfter(g ? docRef.current.blocks[g.end].id : null)
        setDropSide(null)
        setDropBefore(undefined)
        return
      }
    }
    // Over the gutter between two columns: a new column there, before the cell after it.
    const gutterAt = image ? null : target.closest?.('[data-gutter]')?.closest('.wr-cell')?.querySelector('[data-drop-after]')?.getAttribute('data-drop-after')
    const beside = gutterAt ? gutterTarget(gutterAt) : null
    setDropGutter(beside ? gutterAt! : null)
    if (beside) {
      setDropAfter(beside)
      setDropSide('left')
      setDropBefore(undefined)
      return
    }
    // The nearest row, or the top of a column cell (after its marker).
    const row = target.closest?.('[data-row], [data-drop-after]')
    const id = row?.getAttribute('data-row') ?? row?.getAttribute('data-drop-after') ?? lastColumnId()
    setDropAfter(id)
    // As in Composer v3: the outer fifth of a block either side makes a new column there;
    // otherwise the top half drops above it and the bottom half below.
    let side: Side | null = null
    let before: string | null | undefined = undefined
    const isRow = !!row?.hasAttribute('data-row') && !!id
    const besideOk =
      isRow &&
      !target.closest?.('.wr-aside-right, .wr-aside-left, .wr-aside-left-track, .wr-aside-right-track') &&
      id !== draggingRef.current &&
      (image ? canWrapBeside(docRef.current.blocks, id!) : canPlaceBeside(docRef.current.blocks, id!) && !besideAWrap(row))
    if (isRow && row) {
      const r = row.getBoundingClientRect()
      const zone = r.width * 0.22
      if (besideOk && e.clientX < r.left + zone) side = 'left'
      else if (besideOk && e.clientX > r.right - zone) side = 'right'
      else if (e.clientY < r.top + r.height / 2) {
        const prev = afterForBefore(docRef.current.blocks, id!)
        // Nothing before the first block to go after: below it instead.
        if (prev) before = prev
      }
    }
    setDropSide(side)
    setDropBefore(before)
  }
  /** The block a drop on the gutter before cell `markerId` goes beside (on its left), when there's room for a column. */
  const gutterTarget = (markerId: string): string | null => {
    const bs = docRef.current.blocks
    const g = groupOf(bs, markerId)
    const cell = g?.cells.find((c) => c.markerId === markerId)
    const first = cell && bs.slice(cell.from, cell.to).find((b) => !isOutOfFlow(b))
    return first && first.id !== draggingRef.current && canPlaceBeside(bs, first.id) ? first.id : null
  }
  const onDrop = (e: DragEvent) => {
    const side = dropSide
    const after = !side && dropBefore !== undefined ? dropBefore : dropAfter
    setDropAfter(null)
    setDropSide(null)
    setDropWrap(false)
    setDropGutter(null)
    setDropBefore(undefined)
    draggingRef.current = null
    if (locked) return
    const moved = e.dataTransfer.getData(BLOCK_MIME)
    if (moved) {
      e.preventDefault()
      blockOp((bs) => {
        if (bs.find((b) => b.id === moved)?.type === 'image') {
          // An image wraps beside the block at whose edge it's dropped, and never goes into Columns.
          return side && after ? wrapBeside(bs, moved, after, side) : moveGroup(bs, moved, imageDropTarget(bs, after))
        }
        return side && after ? moveBeside(bs, moved, after, side) : moveGroup(bs, moved, after)
      })
      return
    }
    const raw = e.dataTransfer.getData(THOUGHT_MIME)
    if (!raw) return
    e.preventDefault()
    try {
      void insertThought(JSON.parse(raw) as Thought, after, side)
    } catch {
      /* not a thought */
    }
  }

  /** Clicking dotted unfold words, or words a margin note is about, shows their card. */
  const onMainClick = (e: MouseEvent) => {
    const target = e.target as Element
    const span = target.closest?.('span[data-unfold]')
    const u = span && unfoldFor(docRef.current.blocks, span.getAttribute('data-unfold') ?? '')
    const noteMark = !u && target.closest?.('mark[data-note]')
    const anchor = noteMark ? noteMark.getAttribute('data-note') : null
    const note = anchor ? docRef.current.blocks.find((b) => b.type === 'margin' && b.anchor === anchor) : undefined
    const card = u || note
    if (card && !window.getSelection()?.toString()) {
      editor.setSelectedId(card.id)
      selectableEl(card.id)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    }
  }

  // ── Leaving for preview / publish; history ─────────────────────────────
  const goPreview = async (publish: boolean) => {
    try {
      await saveNow()
    } catch {
      return // the save failure is already on screen; stay with the edits
    }
    navigate(`/w/${initial.id}/preview${publish ? '?publish=1' : ''}`)
  }

  const toggleFocus = () => {
    const next = !focusMode
    setFocusMode(next)
    if (next) setPanelOpen(false)
  }

  // Focus mode: the block being typed in stays lit, else the one under the mouse, else mid-screen (useFocusLine). Esc
  // leaves it, unless a menu, form or dialog has the key.
  useFocusLine(pageRef, focusMode && !locked)
  useEffect(() => {
    if (!focusMode) return
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if ((e.target as HTMLElement | null)?.closest?.('[role="menu"], [role="listbox"], [role="dialog"], form, input, textarea')) return
      setFocusMode(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [focusMode])

  // Under md (a narrowed desk window) the thoughts panel lies over the page rather than beside it, from the tool
  // bar's foot down — which rises as Burrow's nav scrolls away.
  const overlaidPanel = desk && !wideDesk && panelOpen && !focusMode
  useEffect(() => {
    if (!overlaidPanel) return
    const read = () => {
      const bar = document.querySelector('section[aria-label="Writing tools"]')
      if (bar) setBarFoot(Math.max(0, bar.getBoundingClientRect().bottom))
    }
    read()
    window.addEventListener('scroll', read, { passive: true })
    window.addEventListener('resize', read)
    return () => {
      window.removeEventListener('scroll', read)
      window.removeEventListener('resize', read)
    }
  }, [overlaidPanel])

  // ── Undo / redo for block operations (blockHistory.ts) ─────────────────
  /** Undo (or redo) the last block operation, remounting any paragraph whose words it changed. */
  const stepHistory = (dir: 'undo' | 'redo') => {
    const touched = dir === 'undo' ? undo() : redo()
    if (!touched) return
    for (const id of touched) editor.bumpRev(id)
    setCropping(null)
  }
  const stepRef = useRef(stepHistory)
  stepRef.current = stepHistory
  // ⌘Z / Ctrl+Z and ⇧⌘Z / Ctrl+Y. Typing keeps the browser's own undo: in a
  // paragraph, the title or any text box, the key is ours only while no text
  // has changed since the last block operation (redo: only straight after our
  // undo) — see `undoOwner`. Off the text (a selected image, the page), it's
  // always ours. Not under History, the audio dock or a dialog.
  useEffect(() => {
    if (locked || historyOpen) return
    const onKey = (e: globalThis.KeyboardEvent) => {
      const kind = undoKey(e)
      if (!kind || e.defaultPrevented) return
      const target = e.target as Element | null
      if (target?.closest?.('[role="dialog"]')) return
      if (undoOwner({ kind, inTextField: isTextField(target) }, undoState()) !== 'ours') return
      e.preventDefault()
      stepRef.current(kind)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [locked, historyOpen, undoState])

  const onRestored = (w: Writing) => {
    replace(w)
    if (titleRef.current) titleRef.current.textContent = w.title
    if (subtitleRef.current) subtitleRef.current.textContent = w.subtitle
    editor.reset()
    setPicker(null)
    setSourceFormFor(null)
    setCropping(null)
    setGen((g) => g + 1)
  }

  // ── Render ─────────────────────────────────────────────────────────────
  const words = countWords(doc.title, doc.blocks)
  const cellIds = new Set(columnGroups(doc.blocks).flatMap((g) => doc.blocks.slice(g.start + 1, g.end).map((b) => b.id)))
  const minutes = readMinutes(words)
  const untouched = isUntouched(doc)
  const todayCount = dayCounts[0] ?? 0
  const published = initial.status === 'published'
  const saveLabel =
    saveState === 'error' ? 'couldn’t save' : saveState === 'saved' ? `saved ${timeAgo(savedAt.toISOString(), now)}` : 'saving…'

  // The "+" sits by the current empty line: the one the caret is in, else the empty line at the end.
  const blockById = (id: string | null) => (id ? doc.blocks.find((b) => b.id === id) : undefined)
  const isEmptyLine = (b: Block | undefined) => !!b && b.type === 'text' && isBlockEmpty(b)
  const caretBlock = blockById(editor.caretId)
  const lastBlock = doc.blocks[doc.blocks.length - 1]
  // Not while a margin note is the current block, pinned or not (Niki, 10/2): the "+" belongs to empty lines only.
  const onNote = [editor.selectedId, editor.focusedId].some((id) => blockById(id)?.type === 'margin')
  const plusFor = locked || onNote ? null : isEmptyLine(caretBlock) ? caretBlock!.id : isEmptyLine(lastBlock) ? lastBlock.id : 'end'

  const placeholderFor = (b: Block): string => {
    if (b.type === 'heading') return `Heading ${headingLevel(b)}`
    if (b.type === 'pullquote') return 'Quote'
    // An empty column always says so, or it can't be seen.
    if (cellIds.has(b.id) && editor.focusedId !== b.id) return 'Column'
    if (!(doc.blocks.length === 1 || editor.focusedId === b.id)) return ''
    return untouched && todayCount > 0 ? `Write, or type / to pull in one of today’s ${todayCount} thoughts` : 'Write, or type / for your thoughts'
  }

  /**
   * The column cell `id` sits in, for an image's corner handle to widen
   * (Niki, 10/2: a bigger image makes its column bigger, moving the gutter).
   * It moves the gutter on the cell's right (the left one for the last
   * cell), live on the grid like a gutter drag; null when the cells stack.
   */
  const columnSizerFor = (id: string): ColumnSizer | null => {
    const bs = docRef.current.blocks
    const g = groupOf(bs, id)
    const i = bs.findIndex((x) => x.id === id)
    const ci = g ? g.cells.findIndex((c) => i >= c.from && i < c.to) : -1
    const grid = g && document.querySelector<HTMLElement>(`[data-cols="${g.startId}"] .wr-cols-grid`)
    if (!g || ci < 0 || !grid) return null
    const n = g.cells.length
    // Stacked (a narrow page): one track, no gutter to move.
    if (getComputedStyle(grid).gridTemplateColumns.trim().split(/\s+/).length < n) return null
    const gap = parseFloat(getComputedStyle(grid).columnGap) || 0
    const usable = grid.getBoundingClientRect().width - gap * (n - 1)
    if (!(usable > 0)) return null
    const from = columnWidths(bs[g.start], n) ?? equalWidths(n)
    const [boundary, sign] = ci < n - 1 ? [ci + 1, 1] : [ci, -1]
    let now = from
    const show = (w: number[]) => grid.style.setProperty('--wr-cols-tpl', columnsTemplate(w) ?? '')
    return {
      grow: (px) => {
        now = resizeBoundary(from, boundary, (sign * px) / usable, columnMinShare(n))
        show(now)
      },
      commit: () => {
        if (now.every((w, k) => w === from[k])) return
        const startId = g.startId
        const next = now
        blockOp((xs) => setColumnWidths(xs, startId, next))
      },
      cancel: () => show(from),
    }
  }

  /** A block's grip starts a move: up or down, or beside another block as a column. */
  const dragBlock = (id: string) => (e: DragEvent<HTMLElement>) => {
    e.dataTransfer.setData(BLOCK_MIME, id)
    e.dataTransfer.effectAllowed = 'move'
    draggingRef.current = id
  }

  /**
   * A paragraph, heading or pull quote is a block too (Niki, 10/5): its grip
   * drags it up or down, or beside another block as a column, as a card's
   * does, showing the words themselves under the pointer.
   */
  const dragText = (id: string) => (e: DragEvent<HTMLElement>) => {
    dragBlock(id)(e)
    const el = blockEl(id)
    if (el) e.dataTransfer.setDragImage?.(el, 12, 14)
  }

  const renderColumn = (b: Block, { covered, inCell }: { covered: PinSide | null; inCell: boolean }) => {
    switch (b.type) {
      case 'text':
      case 'heading':
      case 'pullquote':
        return (
          <>
            {/* Level with its first line; not on an empty line, where the "+" is. */}
            {!locked && !isBlockEmpty(b) && (
              <DragGrip
                selected={false}
                reveal="row"
                className={b.type === 'heading' ? 'top-[18px]' : b.type === 'pullquote' ? 'top-10' : 'top-0.5'}
                onDragStart={dragText(b.id)}
              />
            )}
            {b.fromThought && !locked && editor.focusedId === b.id && (
              <BlockToolbar label="Was a thought">
                <ToolbarLabel>Was a card</ToolbarLabel>
                <ToolbarButton onClick={() => backToCard(b)}>Back to card</ToolbarButton>
              </BlockToolbar>
            )}
            <EditableBlockView
              block={b}
              placeholder={placeholderFor(b)}
              className={covered && b.type === 'text' ? `wr-pin-covered ${covered === 'right' ? 'wr-pin-covered-right' : ''}` : ''}
              readOnly={locked}
              {...editable}
            />
            {b.type === 'pullquote' && (!locked || b.attribution) && (
              <QuoteAttributionField
                value={b.attribution ?? ''}
                readOnly={locked}
                onChange={(attribution) => patchBlock(b.id, { attribution: attribution || undefined } as Partial<PullQuoteBlock>)}
              />
            )}
          </>
        )
      case 'thought':
        return (
          <ThoughtBlockView
            {...selectable(b.id)}
            {...sourcing(b.id)}
            block={b}
            showTimes={doc.show_times}
            onConvert={(to) => convertThought(b, to)}
            onDragStart={dragBlock(b.id)}
          />
        )
      case 'image':
        return (
          <ImageBlockView
            {...selectable(b.id)}
            onSelect={(focus) => editor.selectBlock(b.id, focus)}
            block={b}
            showTimes={doc.show_times}
            onCaption={(caption) => patchBlock(b.id, { caption })}
            // A wrap's offset is the hand's; a toolbar or arrow-key move starts it level with the paragraph again.
            onPlace={(placement) => patchBlock(b.id, { placement, offset: undefined } as Partial<ImageBlock>, true)}
            onCrop={() => setCropping({ kind: 'block', id: b.id })}
            onReplace={() => {
              replaceImageFor.current = b.id
              replaceInput.current?.click()
            }}
            onReplaceFile={(file) => void replaceImage(b.id, file)}
            onResize={(width) => blockOp((bs) => bs.map((x) => (x.id === b.id && x.type === 'image' ? sizedIn(x, width, inCell) : x)))}
            onReshape={(frame, width) =>
              blockOp((bs) => bs.map((x) => (x.id === b.id && x.type === 'image' ? sizedIn(withFrame(x, frame), width, inCell) : x)))
            }
            canWrap={!inCell}
            columnSizer={inCell ? () => columnSizerFor(b.id) : undefined}
            // One drag of the hand is one undo, however many places it tried on the way.
            onHandStart={gesture.begin}
            onHandMove={(t) => blockOp((bs) => placeImage(bs, b.id, t.before, t.placement, t.offset))}
            onHandEnd={gesture.end}
            onHandCancel={gesture.cancel}
            // The view shows the photo itself under the pointer (BlockViews photoGhost).
            onDragStart={dragBlock(b.id)}
          />
        )
      case 'linkcard':
        return <LinkCardBlockView {...selectable(b.id)} block={b} onChange={(patch) => patchBlock(b.id, patch)} onDragStart={dragBlock(b.id)} />
      case 'collection':
        return (
          <>
            <CollectionBlockView {...selectable(b.id)} block={b} onChange={() => setCollectionPick({ afterId: b.id, replace: b.id })} onDragStart={dragBlock(b.id)} />
            {collectionPick?.replace === b.id && <CollectionPicker onPick={addCollectionCard} onCancel={() => setCollectionPick(null)} />}
          </>
        )
      case 'divider':
        return <DividerView {...selectable(b.id)} block={b} />
      case 'scene': {
        const s = selectable(b.id)
        return (
          <SceneView
            {...s}
            onSelect={(focus) => editor.selectBlock(b.id, focus)}
            block={b}
            onChange={(patch) => patchBlock(b.id, patch)}
            onPickImage={() => {
              sceneImageFor.current = b.id
              document.getElementById('wr-scene-image')?.click()
            }}
            onCrop={() => setCropping({ kind: 'block', id: b.id })}
          />
        )
      }
      default:
        return null
    }
  }

  /** A margin note, pinned or not: pinned, it carries the paragraph count (it renders in the left track). */
  const renderNote = (b: MarginNoteBlock) => {
    const max = maxPinSpan(doc.blocks, b.id)
    return (
      <MarginNoteView
        {...selectable(b.id)}
        {...sourcing(b.id)}
        block={b}
        onText={(text, html) => setNoteWords(b.id, text, html)}
        onLinkKey={() => {
          const found = readSelection()
          if (!found || !found.note || found.sel.blockId !== b.id || locked) return false
          openLink(found)
          return true
        }}
        onIntoColumn={() => toColumn(b.id)}
        onFromThoughts={b.anchor ? () => setPicker({ kind: 'margin', afterId: asideAnchor(docRef.current.blocks, b.id), fill: b.id, words: b.words }) : undefined}
        pin={
          isPinnedNote(b)
            ? {
                count: pinnedParagraphCount(doc.blocks, b),
                max,
                onSpan: (span) => blockOp((bs) => setPinSpan(bs, b.id, span)),
                side: pinSide(b),
                onSide: (side) => blockOp((bs) => setPinSide(bs, b.id, side)),
                onUnpin: () => blockOp((bs) => setPinSpan(bs, b.id, null)),
              }
            : undefined
        }
        onPin={!locked && max > 0 ? () => pinNote(b.id) : undefined}
      />
    )
  }

  const renderAside = (b: MarginNoteBlock | VoiceBlock) =>
    b.type === 'margin' ? (
      renderNote(b)
    ) : (
      <VoiceMemoView
        block={b}
        authorName={authorName}
        editable
        defaultOpen
        onChange={(next) => setBlocks((bs) => bs.map((x) => (x.id === b.id ? next : x)))}
        onRemove={() => removeBlock(b.id)}
        onRerecord={() => dock.openMemo({ afterId: null, voiceId: b.id, anchorId: asideAnchor(docRef.current.blocks, b.id) })}
      />
    )

  const renderPin = (pin: MarginNoteBlock) => renderNote(pin)

  const renderUnfold = (u: UnfoldBlock) => (
    <UnfoldCard {...selectable(u.id)} {...sourcing(u.id)} block={u} onText={(text) => patchBlock(u.id, { text })} onDetach={() => toColumn(u.id)} />
  )

  const renderGutter = ({ startId, markerId, boundary, widths }: { startId: string; markerId: string; boundary: number; widths: number[] }) => (
    <ColumnGutter
      boundary={boundary}
      widths={widths}
      active={dropGutter === markerId}
      dragging={dropAfter !== null}
      onResize={(next) => blockOp((bs) => setColumnWidths(bs, startId, next))}
    />
  )

  /** Hovering just outside a block's edge: "+" makes an empty column on that side, and the caret goes into it. */
  const renderBeside = (blockId: string, sides: Side[]) =>
    // Not beside an image: that would put the image in a column (Niki, 10/5: images wrap, never columns).
    canPlaceBeside(doc.blocks, blockId) && doc.blocks.find((b) => b.id === blockId)?.type !== 'image' ? (
      <AddColumnBeside
        sides={sides}
        onAdd={(side) => {
          const nb: Block = { id: newBlockId(), type: 'text', html: '' }
          blockOp((bs) => placeBeside(bs, blockId, side, [nb]))
          editor.focusLater(nb.id, 'start')
        }}
      />
    ) : null

  const insertMenu = (afterId: string | null) => (
    <InsertMenu
      exclude={groupOf(doc.blocks, afterId) ? NOT_IN_A_CELL : undefined}
      onChoose={(c) => insertChoice(c, afterId)}
    />
  )

  const pickerFor = (afterId: string | null) =>
    picker && picker.afterId === afterId ? (
      <ThoughtPicker
        key={`${picker.kind}:${afterId}:${picker.fill ?? ''}`}
        kind={picker.kind}
        words={picker.words}
        onPick={(t) => onPicked(picker, t)}
        onOwn={(text) => onPicked(picker, null, text)}
        onCancel={() => setPicker(null)}
      />
    ) : null

  const renderExtras = (id: string) => (
    <>
      {editor.slash?.blockId === id && (
        <SlashMenu
          query={editor.slash.query}
          thoughts={editor.slashThoughts}
          index={Math.min(editor.slash.index, Math.max(0, (editor.slashThoughts?.length ?? 0) - 1))}
          onChoose={editor.chooseSlash}
          onHover={editor.setSlashIndex}
          onBrowse={() => searchThoughts(editor.closeSlash()?.trim() ?? '')}
        />
      )}
      {plusFor === id && !editor.slash && insertMenu(id)}
      {pickerFor(id)}
      {linkFormAfter === id && <LinkForm onAdd={addLinkCard} onCancel={() => setLinkFormAfter(undefined)} />}
      {collectionPickerAt(id)}
      {dropAfter === id &&
        !dropGutter &&
        (dropSide ? (
          dropWrap ? (
            <WrapDropMark side={dropSide} />
          ) : (
            <GutterDropMark side={dropSide} />
          )
        ) : (
          <BlockDropLine above={dropBefore !== undefined} />
        ))}
    </>
  )

  const panel = (
    <ThoughtsPanel
      blocks={doc.blocks}
      totalCount={totalThoughts}
      dayCounts={dayCounts}
      untouched={untouched}
      searchRef={panelSearchRef}
      seedQuery={panelSeed}
      onInsert={(t) => {
        void insertThought(t, editor.selectedId ?? editor.lastEditable.current)
        if (!desk) setPanelOpen(false)
      }}
      onReflectDay={(d) => void reflectOnDay(d)}
      onShowUsed={showUsed}
      onClose={() => setPanelOpen(false)}
    />
  )

  /** On a desk the panel is a column beside the page while it's open; there's no column when it's closed. */
  const railed = desk && !focusMode && panelOpen
  const crop = cropSubject()
  const byline = `${longDate(initial.published_at ?? now)} · ${minutes} min read`
  const dockLabel =
    dock.mode === 'record'
      ? dock.session?.phase === 'paused'
        ? 'Recording · paused'
        : 'Recording'
      : dock.mode === 'memo'
        ? 'Voice memo'
        : dock.syncing
          ? 'Syncing'
          : 'Listening'
  /** Locked content keeps its place on screen but can't be reached (a React 18 runtime: `inert` as an attribute). */
  const lockRef = (el: HTMLElement | null) => {
    el?.toggleAttribute('inert', locked)
  }

  return (
    <div className="min-h-dvh bg-canvas">
      {/* Burrow's nav scrolls away with the page; only the writing's tool bar stays pinned. It steps aside in Focus mode. */}
      {!focusMode && (
        <div ref={lockRef} className="contents">
          <BurrowNav sticky={false} onSearch={searchThoughts} />
        </div>
      )}
      <ComposerToolBar
        status={`${published ? 'Published' : 'Draft'} · ${saveLabel}`}
        statusTitle={published ? 'Edits to a published writing go live as they save.' : undefined}
        published={published}
        focusMode={focusMode}
        historyOpen={historyOpen}
        onHistory={() => setHistoryOpen(true)}
        onFocus={toggleFocus}
        onPreview={() => void goPreview(false)}
        onPublish={() => void goPreview(true)}
        showCount={showCount}
        onShowCount={setShowCount}
        dock={locked ? { label: dockLabel, live: dock.session?.phase === 'recording', onExit: dock.requestExit } : null}
        undo={{ canUndo, canRedo, onUndo: () => stepHistory('undo'), onRedo: () => stepHistory('redo') }}
        thoughts={desk ? { open: panelOpen, today: todayCount, onToggle: () => (panelOpen ? setPanelOpen(false) : openPanel()) } : null}
      />
      {/* A narrowed desk window (lib/layout.ts) gives the panel a little less, so the page keeps room to write; under md it lies over the page instead. */}
      <div className={railed ? 'md:grid md:grid-cols-[minmax(0,1fr)_300px] lg:grid-cols-[minmax(0,1fr)_340px]' : ''}>
        {/* Inert under History (see the effect above), so nothing typed behind the sheet races a restore. */}
        <main
          ref={pageRef}
          className="wr-page relative min-w-0"
          onDragOver={onDragOver}
          onDrop={onDrop}
          onDragLeave={(e) => {
            // Only when the drag leaves the page, not as it crosses from one block to the next.
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
            setDropAfter(null)
            setDropSide(null)
            setDropWrap(false)
            setDropGutter(null)
            setDropBefore(undefined)
          }}
          onClick={onMainClick}
        >
          {showCount && !focusMode && (
            // Small and out of the way in the page's corner (composer lab 04); the tool bar's "···" hides it.
            // On a desk it sits just left of the floating Thoughts square, level with it.
            <p
              className={`pointer-events-none absolute m-0 font-sans text-[12px] font-medium tabular-nums text-muted-foreground ${
                desk ? 'right-[72px] top-[23px] sm:right-[80px]' : 'right-4 top-3 sm:right-6 sm:top-4'
              }`}
            >
              {`${words.toLocaleString()} words · ${minutes} min`}
            </p>
          )}
          {/* With the dock open it rides at the end of the column, sticky to the bottom, so the last lines are never under it. */}
          <div className={`px-4 sm:px-6 ${focusMode && !locked ? 'pb-40 pt-6 sm:pt-10' : `pt-8 sm:pt-14 ${locked ? 'pb-4 max-sm:pb-0' : 'pb-40'}`}`}>
            <div className="wr-column">
              <input
                ref={coverInput}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                aria-label="Cover image file"
                hidden
                onChange={(e) => {
                  void setCover(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
              <input
                ref={imageInput}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                hidden
                onChange={(e) => {
                  void onImagePicked(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
              <input
                ref={replaceInput}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                aria-label="Replacement image file"
                hidden
                onChange={(e) => {
                  void replaceImage(replaceImageFor.current, e.target.files?.[0])
                  e.target.value = ''
                }}
              />
              <input
                id="wr-scene-image"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                hidden
                onChange={(e) => {
                  void onSceneImagePicked(e.target.files?.[0])
                  e.target.value = ''
                }}
              />

              <ComposerHeader
                coverUrl={doc.cover_url}
                coverFrame={doc.cover_frame}
                coverBusy={coverBusy}
                onPickCover={() => coverInput.current?.click()}
                onDropCover={(f) => void setCover(f)}
                onRemoveCover={() => update((d) => ({ ...d, cover_url: null, cover_frame: null }))}
                onCropCover={() => setCropping({ kind: 'cover' })}
                titleRef={titleRef}
                subtitleRef={subtitleRef}
                onTitleInput={onHeaderInput('title')}
                onSubtitleInput={onHeaderInput('subtitle')}
                onTitleKeyDown={onTitleKeyDown}
                onSubtitleKeyDown={onSubtitleKeyDown}
                avatarUrl={profile?.avatar_url}
                authorName={authorName}
                byline={byline}
                showTimes={doc.show_times}
                onShowTimes={(v) => update((d) => ({ ...d, show_times: v }))}
                locked={locked}
                focusMode={focusMode && !locked}
                narration={
                  <NarrationCard
                    narration={doc.narration}
                    authorName={authorName}
                    blocks={doc.blocks}
                    onChange={setNarration}
                    onRecord={dock.openRecord}
                    onListen={() => dock.openListen({ play: true })}
                    onSync={() => dock.openListen({ sync: true })}
                    docked={locked}
                    dockNote={dock.mode === 'memo' ? 'A voice memo is open in the dock below' : undefined}
                  />
                }
              />

              <BlockList
                // A restored version remounts every block.
                key={gen}
                blocks={doc.blocks}
                rev={editor.rev}
                dock={dock.view}
                renderColumn={renderColumn}
                renderAside={renderAside}
                renderPin={renderPin}
                renderUnfold={renderUnfold}
                renderExtras={renderExtras}
                renderGutter={renderGutter}
                renderBeside={renderBeside}
              />
              {!locked && pickerFor(null)}
              {!locked && linkFormAfter === null && <LinkForm onAdd={addLinkCard} onCancel={() => setLinkFormAfter(undefined)} />}
              {!locked && collectionPickerAt(null)}
              {plusFor === 'end' && <div className="relative h-8">{insertMenu(null)}</div>}
              <AudioDock dock={dock} authorName={authorName} />
            </div>
          </div>
        </main>

        {railed && (
          <div ref={lockRef} className="contents">
            <aside
              aria-label="Thoughts"
              style={overlaidPanel ? { top: barFoot } : undefined}
              className="top-12 border-l border-border/40 bg-sidebar max-md:fixed max-md:bottom-0 max-md:right-0 max-md:z-30 max-md:w-[300px] max-md:shadow-[-18px_0_40px_rgba(45,27,30,.12)] md:sticky md:h-[calc(100dvh-3rem)]"
            >
              {panel}
            </aside>
          </div>
        )}
      </div>

      {!desk && panelOpen && !locked && (
        <MobileSheet label="Thoughts" sidebar onClose={() => setPanelOpen(false)}>
          {panel}
        </MobileSheet>
      )}
      {/* Centred on the bottom edge (Niki, 10/2); it steps aside for the Fix walk's bar, which sits there too. */}
      {!desk && !panelOpen && !focusMode && !locked && !sourceWalk.active && (
        <SheetFab onClick={() => setPanelOpen(true)} aria-label="Open thoughts" className="wr-thoughts-fab">
          Thoughts
        </SheetFab>
      )}

      {selection && !picker && !linking && !locked && (
        <SelectionToolbar
          box={selection.box}
          format={selection.format}
          block={blockKindOf(doc.blocks.find((b) => b.id === selection.sel.blockId))}
          link={selection.link}
          canNote={selection.canNote}
          linkOnly={selection.note}
          onFormat={(action) => formatSelection(selection.sel, action)}
          onBlock={(to) => convertEditable(selection.sel, to)}
          onLink={() => openLink(selection)}
          onNote={() => noteOnWords(selection.sel)}
        />
      )}
      {linking && !locked && (
        <LinkEditor
          box={linking.box}
          initial={linking.href}
          onApply={applyLink}
          onCancel={() => {
            const l = linking
            setLinking(null)
            reselect(l.sel)
          }}
        />
      )}

      {crop && (
        <ImageEditor
          open
          src={crop.src}
          frame={crop.frame}
          target={crop.target}
          onClose={() => setCropping(null)}
          onDone={applyCrop}
        />
      )}
      <SourceWalkBar
        walk={sourceWalk}
        onPublish={() => {
          sourceWalk.stop()
          void goPreview(true)
        }}
      />
      <HistoryPanel writingId={initial.id} open={historyOpen} onClose={() => setHistoryOpen(false)} flush={saveNow} onRestored={onRestored} />
    </div>
  )
}

/**
 * An image's new width. In a column cell a wrap shows (and sizes) as a column
 * image, so its width is taken as one there, not clamped to a wrap's 60%; the
 * stored placement waits for it to come back out.
 */
function sizedIn(x: ImageBlock, width: number | null, inCell: boolean): ImageBlock {
  if (!inCell || !(x.placement === 'wrap-left' || x.placement === 'wrap-right')) return withWidth(x, width)
  return { ...withWidth({ ...x, placement: 'inline' }, width), placement: x.placement }
}

/** Where a dragged block or thought will land, between blocks (Composer v3): a plum line above or below this one. */
function BlockDropLine({ above }: { above: boolean }) {
  return above ? (
    <div aria-hidden data-drop-mark="above" className="pointer-events-none absolute inset-x-0 -top-3 z-20 h-0.5 rounded-full bg-primary" />
  ) : (
    // In the flow, half way into the space under the block (also at the top of a column cell).
    <div aria-hidden data-drop-mark="below" className="pointer-events-none relative z-20 -mt-3 mb-2.5 h-0.5 rounded-full bg-primary" />
  )
}

/**
 * Where a dragged image will land as a wrap (Niki, 10/5: images wrap, never
 * columns): a faint photo-sized box on that side of the block, its text
 * left where it is to run beside it.
 */
function WrapDropMark({ side }: { side: Side }) {
  return (
    <div
      aria-hidden
      data-drop-mark={`wrap-${side}`}
      className={`pointer-events-none absolute top-0 z-20 flex h-[min(100%,160px)] min-h-16 w-[31%] items-start justify-center rounded-md border-2 border-dashed border-primary/70 bg-[color-mix(in_srgb,var(--primary)_12%,var(--canvas))] pt-2 font-sans text-[11px] font-semibold text-primary ${
        side === 'left' ? 'left-0' : 'right-0'
      }`}
    >
      Wrap {side}
    </div>
  )
}

/**
 * Where a dragged block or thought will land as a new column (Composer v3):
 * the block it's beside is pushed over to make room (index.css, on
 * [data-drop-mark]), the new column's place shows faintly, and the gutter
 * between them is the plum line with "↔ column" on it.
 */
function GutterDropMark({ side }: { side: Side }) {
  const left = side === 'left'
  return (
    <div aria-hidden data-drop-mark={side} className={`pointer-events-none absolute inset-y-0 z-20 w-[calc(50%-16px)] ${left ? 'left-0' : 'right-0'}`}>
      <div className="absolute inset-0 rounded-md bg-primary/[.035]" />
      <ColumnDropLine className={`inset-y-0 ${left ? '-right-[17px]' : '-left-[17px]'}`} />
    </div>
  )
}
